import { FunctionComponent, useEffect, useRef, useState } from "react";
import "./CourseCategories.scss";
import Table from "../../../Components/Table/Table";
import CustomToolbar from "../../../Components/Table/components/CustomToolbar";
import ButtonField from "../../../Components/Button/ButtonField";
import { Columns } from "../../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { ICourseCategory } from "./ICourseCategory";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import Dialog from "../../../Components/Dialog/Dialog";
import CourseCategoriesForm from "./CourseCategoriesForm";
import ResponseDialog from "../../../Components/ResponseDialog/ResponseDialog";
import TableSearch from "../../../Components/Table/components/TableSearch";
import { ReactComponent as Delete } from "../../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../../Assets/Images/edit.svg";
import { IDeleteDialog } from "../../../Components/DeleteDialog/IDeleteDialog";
import IconButtonField from "../../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import DeleteDialog from "../../../Components/DeleteDialog/DeleteDialog";
import { ReactComponent as RefreshButton } from "../../../Assets/Images/RefreshButton.svg";

import { listCourseCategory } from "../../../Redux/Slices/CourseCategory/ListCourseCategorySlice";
import { addCourseCategory, resetaddCourseCategory } from "../../../Redux/Slices/CourseCategory/AddCourseCategorySlice";


interface CourseCategoriesProps {}

const CourseCategories: FunctionComponent<CourseCategoriesProps> = () => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteDialogData, setDeleteData] = useState<IDeleteDialog>({});
  const [initialValues, setInitialValues] = useState<ICourseCategory | {}>({ status: true });
  
  const dispatch = useAppDispatch();
  const { data: listData } = useAppSelector((state) => state.ListCourseCategorySlice);
  const { data: addData } = useAppSelector((state) => state.AddCourseCategorySlice);

  const permissions = useAppSelector((state) => state.PermissionsToPage);
  const finaldata = { permissions };

  const formRef = useRef<HTMLFormElement>(null);
  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      width: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "name",
      renderHeader: () => <b>Category Name</b>,
      flex: 1,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "description",
      renderHeader: () => <b>Description</b>,
      flex: 1,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      flex: 1,
      type: "string",
      headerAlign: "left",
      cellClassName: (params) => `activename ${params.row.status ? "active" : "inactive"}`,
      valueFormatter: (params) => (params.value === true ? "Active" : "Inactive"),
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: "actions",
      renderCell: (params: GridRenderCellParams<ICourseCategory>) => {
        return (
          <>
            <IconButtonField
              disabled={finaldata?.permissions?.edit === false}
              onClick={() => {
                setInitialValues({ ...params.row, id: params.id as number });
                setIsEdit(true);
                setDialogOpen(true);
              }}
            >
              <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
            </IconButtonField>
            <IconButtonField
              disabled={finaldata?.permissions?.delete === false}
              onClick={() => {
                setDeleteData({
                  ...params.row,
                  id: params.id as number
                });
                setDeleteDialogOpen(true);
              }}
            >
              <SvgIcon component={Delete} inheritViewBox />
            </IconButtonField>
          </>
        );
      },
    },
  ];

  // Assuming api response format lists categories in `data: [...]` but based on standard wrapper might be `listData?.data`
  const tableData = Array.isArray(listData?.data)
    ? listData?.data.map((item: any, index: number) => ({ ...item, index: index + 1 }))
    : [];

  useEffect(() => {
    dispatch(listCourseCategory());
  }, [addData, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetaddCourseCategory());
 
    };
  }, [dispatch]);

  return (
    <>
      <Table rows={tableData ?? []} columns={columns} exportFileName="CourseCategories">
        <CustomToolbar>
          <h6></h6>
          <TableSearch />
          <ButtonField
            className="tabel_add_new_button"
            onClick={() => {
              setInitialValues({ status: true });
              setIsEdit(false);
              setDialogOpen(true);
            }}
            toolTip="Add new"
            disabled={finaldata?.permissions?.add === false}
          >
            Add new
          </ButtonField>
          <IconButtonField
            className="refresh_btn"
            toolTip="Refresh"
            onClick={() => dispatch(listCourseCategory())}
          >
            <SvgIcon className="refresh_svg_icon" component={RefreshButton} inheritViewBox />
          </IconButtonField>
        </CustomToolbar>
      </Table>

      <Dialog
        open={dialogOpen}
        dialogClassName="dialog"
        Dialog_Title={`${isEdit ? "Update" : "Add"} Course Category`}
        dialogContentClassName="dialog_content"
        Dialog_Content={
          <CourseCategoriesForm
            formRef={formRef}
            initialValues={initialValues}
            setInitialValues={setInitialValues}
          />
        }
        Dialog_Actions={
          <>
            <ButtonField
              className="form_cancel_button"
              onClick={() => {
                setDialogOpen(false);
                setIsEdit(false);
              }}
            >
              Cancel
            </ButtonField>
            <ButtonField
              className="form_save_button"
              onClick={() => formRef.current?.requestSubmit()}
            >
              Save
            </ButtonField>
          </>
        }
      />
      
      <ResponseDialog response={addData} parentDialog={setDialogOpen} />
      
      <DeleteDialog
        deleteableName={deleteDialogData.name ?? ""}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        deleteFunction={() => {
          if (deleteDialogData) {
            dispatch(addCourseCategory({
              ...deleteDialogData,
              is_delete: true
            }));
          }
          setDeleteDialogOpen(false);
        }}
      />
    </>
  );
};

export default CourseCategories;
